'use client';

import { TriangleAlert, X } from 'lucide-react';
import { useId, useRef, type ReactNode, type RefObject } from 'react';
import { Button } from './button';
import { useDialogFocus } from './focus-contract';
import styles from './dialog.module.css';

/**
 * Diyalog stil haritası — `tableStyles` ile AYNI desen. Paket dışından
 * `@icebreaker/ui/dialog.module.css` diye derin bir yol açmamak için sınıf
 * haritası buradan verilir (admin ekranlarındaki `reason-dialog` kullanır).
 */
export const dialogStyles = styles;

export interface ModalProps {
  readonly open: boolean;
  readonly title: string;
  /** Başlığın üstündeki kicker (referansta katman başlığının üstünde durur). */
  readonly kicker?: string;
  readonly closeLabel: string;
  readonly children: ReactNode;
  /** Alt şerit: solda not, sağda eylemler. */
  readonly footer?: ReactNode;
  /**
   * Varsayılan başlık şeridi yerine geçen özel başlık (ör. gönderildi
   * katmanının vurgu zeminli posteri). Verildiğinde erişilebilir ad `title`
   * üzerinden `aria-label` ile taşınır.
   */
  readonly header?: ReactNode;
  readonly onClose: () => void;
  readonly className?: string;
  /**
   * GENİŞ KATMAN (02.09): varsayılan 560px form katmanları için doğru;
   * çok sütunlu tablo taşıyan katmanlar (ör. admin katılımcı dökümü, 5 sütun)
   * 560'ta son sütunu kırpıyordu — ölçüldü. `wide` 820'ye çıkarır; ölçü
   * KİTİN kararıdır, ekran kendi genişliğini tanımlamaz (page-shell-rail
   * kapısının kuralı).
   */
  readonly wide?: boolean;
  /**
   * KATMANIN PERDE İÇİNDEKİ YERİ (16.09).
   *   · `center` (varsayılan) — ortalanmış form/onay katmanı.
   *   · `side`               — SAĞDAN giren, ekran boyu kenar paneli (420).
   *   · `side-wide`          — aynı kenar paneli, 480 px (panel satış adayı
   *     detayı P2: referans 480 çizer, alan etiketi + değer + "Kopyala" üç
   *     kolonu 420'de e-postayı iki satıra kırıyordu).
   *   · `sheet`              — ALTTAN açılan sayfa (takvime ekle 1f, 17.10.2026):
   *     dar ekranda açılır menünün yerine; tam genişlik, üst köşeler yuvarlak,
   *     tutamaç çizgisi. Anatomi (tavan, kaydırma kabı, Esc, odak) aynıdır.
   *   · `start`              — SOLDAN açılan 300 px çekmece (OrganizerShell ve
   *     MobilWebKabuk kartları, yeni görünüm 1c): organizatör yan menüsü
   *     1024 px altında buna döner. Köşesiz, `background` zemin.
   *
   * Katılımcı dizini 1280 altında detay rayını kenar katmanına çevirir
   * (tasarım §08). Varyant KİTTE yaşar: ekranlar kendi perdesini yazamaz
   * (`modal-anatomy.guard`) ve yazsaydı odak tuzağı, Esc, kaydırma kilidi ve
   * yükseklik tavanı her yüzeyde yeniden kurulurdu.
   */
  readonly placement?: 'center' | 'side' | 'side-wide' | 'sheet' | 'start';
  /**
   * KAPATMA KİLİDİ (T4 QA P3-2): bir yazma isteği sürerken katman KAPANMAZ.
   * Kapat düğmesi devre dışı çizilir, Esc ve perde tıklaması yok sayılır.
   * Aksi hâlde istek sürerken kapanan katman yanıtı (başarı ya da hata)
   * kimseye göstermez ve personel aynı işi yeniden başlatır.
   */
  readonly closeDisabled?: boolean;
  /** Açılışta odaklanacak öğe (`useDialogFocus`); verilmezse panelin kendisi. */
  readonly initialFocus?: (panel: HTMLElement) => HTMLElement | null;
  /** Kapanışta odağın döneceği açan düğme (fare tıklaması onu her tarayıcıda odaklamaz). */
  readonly returnFocusTo?: RefObject<HTMLElement | null>;
}

/**
 * Genel amaçlı modal (README 4.5: 16px köşe, `lg` derinlik, ink perde).
 * `ConfirmDialog`'dan farkı: içerik serbesttir ve gövde KAYDIRILIR — form
 * taşıyan katmanlar (görüşme talebi) bunu kullanır, kendi perdesini yazmaz.
 *
 * Esc kapatır; açılışta odak panele girer ve Tab panelin İÇİNDE döner
 * (arka plandaki liste klavyeyle gezilemez).
 */
export function Modal({
  open,
  title,
  kicker,
  closeLabel,
  children,
  footer,
  header,
  onClose,
  className,
  wide,
  placement = 'center',
  closeDisabled = false,
  initialFocus,
  returnFocusTo,
}: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const side = placement === 'side' || placement === 'side-wide';
  const sheet = placement === 'sheet';
  const start = placement === 'start';
  const requestClose = closeDisabled ? noop : onClose;

  useDialogFocus({
    open,
    panelRef,
    onEscape: requestClose,
    lockScroll: true,
    initialFocus,
    returnFocusTo,
  });

  if (!open) return null;
  return (
    <div
      className={[
        styles.backdrop,
        side ? styles.backdropSide : '',
        sheet ? styles.backdropSheet : '',
        start ? styles.backdropStart : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={requestClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={header ? undefined : titleId}
        aria-label={header ? title : undefined}
        tabIndex={-1}
        className={[
          styles.panel,
          styles.modal,
          wide ? styles.modalWide : '',
          side ? styles.modalSide : '',
          placement === 'side-wide' ? styles.modalSideWide : '',
          sheet ? styles.modalSheet : '',
          start ? styles.modalStart : '',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={(event) => event.stopPropagation()}
      >
        {sheet ? <span className={styles.sheetGrip} aria-hidden="true" /> : null}
        {header ?? (
          <header className={styles.modalHead}>
            <div className={styles.modalHeadText}>
              {kicker ? <p className={styles.modalKicker}>{kicker}</p> : null}
              <h2 id={titleId} className={styles.title}>
                {title}
              </h2>
            </div>
            {/* İkincil ağırlık: çerçevesiz ikon, 44px hedef (dialog.module.css). */}
            <Button
              variant="ghost"
              className={styles.modalClose}
              aria-label={closeLabel}
              disabled={closeDisabled}
              onClick={requestClose}
            >
              <X size={18} aria-hidden="true" />
            </Button>
          </header>
        )}
        <div className={styles.modalBody}>{children}</div>
        {footer ? <footer className={styles.modalFoot}>{footer}</footer> : null}
      </div>
    </div>
  );
}

/** Onay diyaloğunun eylem türü; `confirmToneVariant` düğme varyantına çevirir. */
export type ConfirmTone = 'positive' | 'negative' | 'irreversible';

const CONFIRM_TONE_VARIANT: Readonly<Record<ConfirmTone, 'primary' | 'dangerOutline' | 'danger'>> =
  {
    positive: 'primary',
    negative: 'dangerOutline',
    irreversible: 'danger',
  };

/** Onay türünün düğme varyantı (panelin gerekçeli onay diyaloğu da aynı eşlemeyi kullanır). */
export function confirmToneVariant(tone: ConfirmTone): 'primary' | 'dangerOutline' | 'danger' {
  return CONFIRM_TONE_VARIANT[tone];
}

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly body?: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  /**
   * ONAY DÜĞMESİNİN TÜRÜ (düğme standardı, Claude Design 19.10.2026). ZORUNLU:
   * eski `danger` bayrağı riskli her onayı kırmızı DOLGUYLA çiziyordu; spec'te
   * kırmızı dolgu yalnız GERİ ALINAMAZ son onaydır.
   *
   *   positive      olumlu onay (Gönder, Engeli kaldır)        → `primary`
   *   negative      geri alınabilir olumsuz onay (Askıya al,
   *                 Daveti iptal et, Kaydı iptal et, Engelle)  → `dangerOutline`
   *   irreversible  geri alınamaz son onay (Sil, metni "geri
   *                 alınamaz" diyen eylem)                     → `danger`
   *
   * `irreversible` kullanımı dosya başına defterdedir
   * (`packages/shared/src/design/dugme-rolleri.guard.spec.ts`).
   */
  readonly tone: ConfirmTone;
  /**
   * İstek sürerken onay düğmesi loading durumuna geçer (README 4.8); vazgeç
   * açık kalır — kullanıcı beklemeden çıkabilir. Diyalog, çağıran taraf istek
   * BAŞARILI olmadan kapatılmamalıdır (QA 7A P2-1: iyimser kapanış yasak).
   */
  readonly loading?: boolean;
  /**
   * İstek BAŞARISIZ olduğunda eylemlerin üstünde basılan metin (409/429/403/404).
   * Diyalog açık kalır, kullanıcının seçimi korunur ve düğme yeniden denenebilir
   * (Blok 13 Orta-2: bu akışlarda hata sessizdi, kullanıcı "hiçbir şey olmadı"
   * görüp tekrar basıyordu).
   */
  readonly error?: ReactNode;
  /**
   * Onay düğmesi kapalı, vazgeç açık. Hedef bu arada değiştiyse (ör. davet kabul
   * edildi, satır kalktı) aynı istek aynı hatayı alır; cümle `error`da kalır.
   */
  readonly confirmDisabled?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * Onay penceresi (Görsel Dil v2). Escape kapatır; açılışta odak iptal düğmesine
 * gider (yıkıcı eylemde güvenli varsayılan).
 *
 * GERİ ALINAMAZ TON (yeni görünüm Dialog kartı, Aşama 1a QA P2-2): panelin
 * rolü `alertdialog` olur, üstte uyarı simgesi çizilir ve örtü tıklaması
 * KAPATMAZ; Esc ve Vazgeç kapatmaya devam eder. Öbür iki tonda rol `dialog`,
 * simge yok, örtü tıklaması Vazgeç ile aynıdır.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone,
  loading = false,
  error,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);

  const irreversible = tone === 'irreversible';

  useDialogFocus({ open, panelRef, onEscape: onCancel, lockScroll: true });

  if (!open) return null;
  return (
    <div className={styles.backdrop} onClick={irreversible ? undefined : onCancel}>
      <div
        ref={panelRef}
        role={irreversible ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={styles.panel}
        onClick={(event) => event.stopPropagation()}
      >
        {irreversible ? (
          <span className={styles.mark} aria-hidden="true">
            <TriangleAlert size={19} />
          </span>
        ) : null}
        <p id={titleId} className={styles.title}>
          {title}
        </p>
        {body ? <p className={styles.body}>{body}</p> : null}
        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
        <div className={styles.actions}>
          {/* Açılışta odak güvenli eyleme gider (autoFocus). */}
          <Button autoFocus variant="ghost" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant={confirmToneVariant(tone)}
            loading={loading}
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function noop(): void {
  /* Kapatma kilitliyken Esc ve perde tıklaması yok sayılır. */
}
